import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {EndlessList} from 'components/ui-kit/EndlessList';
import {
    listState$,
    sortingChanged,
    filtersChanged,
    Gate,
} from 'models/documents/checks/detail/products/list';
import {commonDataState$} from 'models/documents/details';
import {ProductContentAccessors, ProductContentFormattedRow, ProductContentRawRow} from 'types';

import {ListRow, FlexRowElement, Text, RowElement} from './styled';

interface Props {
    receiptId: string;
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: ProductContentRawRow[] | undefined,
        t: TFunction,
    ) => ProductContentFormattedRow[];
}

export const ProductsList = ({receiptId, configFunc, dataMapper}: Props) => {
    const {t} = useTranslation();
    const {data: commonData} = useStore(commonDataState$);
    const {data, sorting, filters, pageIncremented} = useStore(listState$);
    const mappedData = React.useMemo(() => dataMapper(data, t), [dataMapper, data, t]);

    return (
        <>
            <Gate receiptId={receiptId} />
            <EndlessList<ProductContentFormattedRow>
                rows={mappedData}
                totalRowsCount={commonData?.productsTotalCount}
                hasMore={data.length < (commonData?.productsTotalCount || 0)}
                renderRow={(row: ProductContentFormattedRow) => (
                    <ListRow>
                        <FlexRowElement>
                            <Text>{row[ProductContentAccessors.modifiedDate]}</Text>
                            <Text isGray>{row[ProductContentAccessors.status]}</Text>
                        </FlexRowElement>
                        <RowElement>
                            <Text>{row[ProductContentAccessors.code]}</Text>
                        </RowElement>
                        <RowElement>
                            <Text>{row[ProductContentAccessors.name]}</Text>
                        </RowElement>
                        <RowElement>
                            <Text isGray>
                                {row[ProductContentAccessors.packageType]}
                                {' • '}
                                {row[ProductContentAccessors.manufacturer]}
                            </Text>
                        </RowElement>
                    </ListRow>
                )}
                onNextPage={() => pageIncremented()}
                config={configFunc(t)}
                sorting={sorting}
                filters={filters}
                onFiltersChange={filtersChanged}
                onSortingChange={sortingChanged}
            />
        </>
    );
};
